// RH-PAIE-2C — Sélecteur de poste recherchable, alimenté par le référentiel réel rh.postes
// (GET /api/postes) — remplace le champ texte libre utilisé jusqu'ici pour l'affectation et
// la promotion. onChange reçoit { id, intitule } (ou null si vidé) — jamais un texte libre.
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api';
const VERT = '#1B5E20';
const GRIS_BORD = '#E0E0E0';

let _cachePostes = null;
let _cachePromise = null;
async function chargerPostes() {
  if (_cachePostes) return _cachePostes;
  if (!_cachePromise) {
    _cachePromise = axios.get(`${API}/postes`).then((r) => {
      _cachePostes = r.data?.postes || [];
      return _cachePostes;
    });
  }
  return _cachePromise;
}

function Option({ poste, active, onClick }) {
  return (
    <div
      role="option"
      aria-selected={active}
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      style={{
        padding: '8px 12px', cursor: 'pointer', fontSize: 13,
        background: active ? '#E8F5E9' : 'transparent',
        color: active ? VERT : '#333', fontWeight: active ? 700 : 400,
      }}
    >
      {poste.intitule} <span style={{ color: '#999', fontSize: 11.5 }}>({poste.code})</span>
    </div>
  );
}

// RH-PAIE-2E §7 — departementId/siteId (optionnels) : ne proposent que les postes rattachés à
// ce département/site dans le référentiel. Un poste sans rattachement (site_id/departement_id
// NULL) reste proposé partout — comportement dégradé assumé tant que le référentiel n'est pas
// complété par la DRH (jamais un poste caché arbitrairement).
export default function PosteSelector({ valueId, valueLabel, onChange, label, disabled = false, departementId, siteId }) {
  const [postes, setPostes] = useState([]);
  const [ouvert, setOuvert] = useState(false);
  const [recherche, setRecherche] = useState('');
  const [surligne, setSurligne] = useState(0);
  const boxRef = useRef(null);

  useEffect(() => { chargerPostes().then(setPostes); }, []);

  useEffect(() => {
    function fermerSiExterieur(e) {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOuvert(false);
    }
    document.addEventListener('mousedown', fermerSiExterieur);
    return () => document.removeEventListener('mousedown', fermerSiExterieur);
  }, []);

  const postesCompatibles = postes.filter((p) => {
    if (departementId && p.departement_id && p.departement_id !== departementId) return false;
    if (siteId && p.site_id && p.site_id !== siteId) return false;
    return true;
  });
  const posteActuel = postesCompatibles.find((p) => p.id === valueId);
  const filtres = postesCompatibles.filter((p) =>
    !recherche.trim() || p.intitule.toLowerCase().includes(recherche.toLowerCase()) || p.code.toLowerCase().includes(recherche.toLowerCase())
  );

  const selectionner = (poste) => {
    onChange(poste);
    setOuvert(false);
    setRecherche('');
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOuvert(true); setSurligne((s) => Math.min(s + 1, filtres.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSurligne((s) => Math.max(s - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (filtres[surligne]) selectionner(filtres[surligne]); }
    else if (e.key === 'Escape') { setOuvert(false); }
  };

  return (
    <div ref={boxRef} style={{ position: 'relative' }}>
      {label && <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#444', marginBottom: 4 }}>{label}</label>}
      <input
        role="combobox"
        aria-expanded={ouvert}
        disabled={disabled}
        placeholder="Rechercher un poste (référentiel)…"
        value={ouvert ? recherche : (posteActuel ? `${posteActuel.intitule} (${posteActuel.code})` : (valueLabel || ''))}
        onFocus={() => { setOuvert(true); setRecherche(''); }}
        onChange={(e) => { setRecherche(e.target.value); setSurligne(0); }}
        onKeyDown={onKeyDown}
        style={{ width: '100%', padding: '8px 12px', border: `1px solid ${GRIS_BORD}`, borderRadius: 6, fontSize: 13, boxSizing: 'border-box', background: disabled ? '#f5f5f5' : '#fff' }}
      />
      {ouvert && (
        <div style={{ position: 'absolute', zIndex: 50, top: '100%', left: 0, right: 0, marginTop: 4, background: '#fff', border: `1px solid ${GRIS_BORD}`, borderRadius: 8, boxShadow: '0 6px 24px rgba(0,0,0,0.12)', maxHeight: 260, overflowY: 'auto' }}>
          <Option poste={{ id: '', code: '', intitule: '— Aucun —' }} active={surligne === -1} onClick={() => selectionner(null)} />
          {filtres.length === 0 && <div style={{ padding: 12, fontSize: 12.5, color: '#999' }}>Aucun poste ne correspond. Le référentiel des postes (Paramètres) doit peut-être être complété.</div>}
          {filtres.map((p, i) => (
            <Option key={p.id} poste={p} active={i === surligne} onClick={() => selectionner(p)} />
          ))}
        </div>
      )}
    </div>
  );
}
